<template>
  <div class="pdf-container d-flex flex-column">    
    <div v-if="selectedPDF && selectedPDF.url" class="modal-action-bar_pdf flex-shrink-0">
      <div class="d-flex align-items-center px-3 py-2">
        <div class="mr-auto">
          <b-button @click="onForcePrint" size="md" variant="outline-warning" class="border-1 rounded">
            <i class="fa fa-bolt u-mr-xsmall"></i>
            Force Re-Print
          </b-button>
        </div>
        <div class="ml-auto d-flex align-items-center">
          <!-- Actions Dropdown -->
          <b-dropdown text="Actions" variant="outline-info" size="md" class="mr-3 actions-dropdown rounded">
            <b-dropdown-item @click="downloadPDF">
              <i class="fa fa-download mr-2"></i>
              Download
            </b-dropdown-item>
            <b-dropdown-item @click="openInNewTab">
              <i class="fa fa-window-restore mr-2"></i>
              Open in New Tab
            </b-dropdown-item>
            <b-dropdown-item @click="onOpenInWord">
              <i class="fa fa-file-word-o mr-2"></i>
              Open in MS-Word
            </b-dropdown-item>
            <b-dropdown-divider></b-dropdown-divider>
            <b-dropdown-item @click="onOpenVNextPrint">
              <i class="fa fa-external-link mr-2"></i>
              Open vNext Print Document
            </b-dropdown-item>
          </b-dropdown>
          
          <!-- Print Button -->
          <b-button @click="onPrint" size="md" class="ss-info-bg border-0 mr-2">
            <i class="fa fa-print u-mr-xsmall"></i>
            Print
          </b-button>
          
          <!-- Settings Icon -->
           <a @click.prevent="viewPlaceholders" size="md" class="ml-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="3"></circle>
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
          </svg>
          </a>
        </div>
      </div>
    </div>
    
    <!-- New Print System Notice - Show when using or has used fallback API -->
    <div v-if="usingFallbackAPI || usedFallbackAPI" class="print-system-notice flex-shrink-0">
      <div class="d-flex align-items-center justify-content-center py-2 px-3">
        <i class="fa fa-info-circle mr-2"></i>
        <span class="small">
          We implemented a new print system. Documents may differ from original templates. Please report any issues.
        </span>
      </div>
    </div>
    
    <div v-if="loading" class="loading-holder flex-grow-1 d-flex align-items-center justify-content-center">
      <div class="text-center">
        <img src="@/assets/images/snapsuite-icon.png" alt="" width="50" class="mx-auto" />
        <h5 class="text-gray-500 text-purple-800 loading-dots">{{ loadingMessage }}</h5>
        <div v-if="usingFallbackAPI" class="mt-3">
          <p class="text-warning small">
            <i class="fa fa-exclamation-triangle mr-1"></i>
            Using new print system - this may take up to a minute
          </p>
        </div>
      </div>
    </div>
    
    <div v-if="showPlaceholders || placeholderLoading" class="flex-grow-1" style="overflow-y: auto;">
      <div v-if="placeholderLoading" class="text-center py-5">
        <LoadingScreen  />
      </div>
      <vue-json-to-table v-else :data="placeholderData"></vue-json-to-table>
    </div>
    
    <vue-pdf-app 
      v-if="!loading && !showPlaceholders && pdfURL" 
      theme="dark" 
      :config="config" 
      :pdf="pdfURL" 
      @open="openHandler"
      @error="onPDFError"
      @loading-failed="onLoadingFailed"
      ref="vue-pdf" 
      class="pdf-viewer-container flex-grow-1"
      style="min-height: 0;">
    </vue-pdf-app>
    <div v-else-if="!loading && !showPlaceholders && !pdfURL" class="text-center py-5 flex-grow-1 d-flex align-items-center justify-content-center">
      <div>
        <div v-if="(usingFallbackAPI && fallbackRetryCount >= FALLBACK_MAX_RETRIES) || (retryCount >= MAX_RETRIES && fallbackRetryCount >= FALLBACK_MAX_RETRIES)">
          <i class="fa fa-exclamation-triangle text-warning mb-3" style="font-size: 3rem;"></i>
          <h5 class="text-muted mb-3">Unable to Generate PDF Document</h5>
          <p class="text-muted mb-3">
            Both primary and new print systems are currently unavailable after {{ MAX_RETRIES + FALLBACK_MAX_RETRIES }} attempts.
            Please contact support for assistance.
          </p>
          <div class="mb-3">
            <small class="text-muted">
              Primary API attempts: {{ retryCount }}/{{ MAX_RETRIES }}<br>
              Backup API attempts: {{ fallbackRetryCount }}/{{ FALLBACK_MAX_RETRIES }}
            </small>
          </div>
        </div>
        <div v-else>
          <p class="text-muted">Unable to load PDF. The document may not be ready yet or there may be a network issue.</p>
        </div>
        <b-button @click="retryLoad" variant="outline-primary" class="mt-3">
          <i class="fa fa-refresh mr-2"></i>
          Retry
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
  import VuePdfApp from "vue-pdf-app";
  import LoadingScreen from "@/components/_universal/global/loading-screen.vue";
  import VueJsonToTable from "vue-json-to-table";
  import "vue-pdf-app/dist/icons/main.css";
  import print from "print-js";
  import ApplicationService from "@/services/application";
  import axios from "axios";

  const app = new ApplicationService();

  const getSidebar = () => ({
    viewThumbnail: true,
    viewOutline: false,
    viewAttachments: false,
  });

  export default {
    name: "ViewPdf",
    data() {
      return {
        modalStatus: {},
        config: {
          toolbar: false,
          sidebar: getSidebar(),
        },
        tenantId: "",
        userId: "",
        isAircoolSystem: false,
        loading: false,
        placeholderLoading: false,
        showPlaceholders: false,
        placeholderData: {},
        selectedPDF: null,
        document: null,
        timer: null,
        retryCount: 0,
        MAX_RETRIES: 2, // Only try 2 times before switching to new print system
        FALLBACK_MAX_RETRIES: 30, // Increased to 30 retries for fallback API
        fallbackRetryCount: 0,
        usingFallbackAPI: false,
        usedFallbackAPI: false, // Track if fallback was ever used in this session
        originalPDFUrl: null, // Store original URL for download/print
        blobUrl: null, // Store blob URL for viewing
        originalParams: null, // Store original params to prevent loss during retries
        aircoolTenantId: process.env.VUE_APP_AIRCOOL_TENANT_ID || 'aircool', // Define aircool tenant ID
        loadingMessage: 'Loading PDF document...',
      };
    },
    components: {
      VuePdfApp,
      LoadingScreen,
      VueJsonToTable,
    },
    mounted() {
      this.initializeData();
    },
    beforeDestroy() {
      this.clearTimer();
      // Clean up blob URLs to prevent memory leaks
      if (this.blobUrl) {
        // console.log('Cleaning up blob URL:', this.blobUrl);
        URL.revokeObjectURL(this.blobUrl);
      }
    },
    computed: {
      pdfURL() {
        // Use blob URL if available, otherwise use original URL
        const url = this.blobUrl || (this.selectedPDF && this.selectedPDF.url) || null;
        // console.log('PDF URL for viewer:', url);
        return url;
      },
      // Helper to safely access params
      safeParams() {
        return this.originalParams || (this.selectedPDF && this.selectedPDF.params) || null;
      },
    },
    methods: {
      async validatePDFUrl(url) {
        // console.log('Starting PDF validation for URL:', url);
        
        if (!url) {
          // console.error('No URL provided for validation');
          return false;
        }

        try {
          // console.log(`PDF validation attempt ${this.retryCount + 1}/${this.MAX_RETRIES}`);
          
          // For CORS issues, we'll try to create a blob URL by fetching through a proxy or iframe
          // First try direct access, if it fails due to CORS, we'll use alternative methods
          const response = await axios.get(url, {
            timeout: 15000, // Increased timeout to 15 seconds
            responseType: 'blob',
            headers: {
              'Accept': 'application/pdf',
            }
          });
          
          // console.log('PDF fetch response:', {
          //   status: response.status,
          //   statusText: response.statusText,
          //   contentType: response.headers['content-type'],
          //   dataSize: response.data.size
          // });
          
          if (response.status === 200 && response.data.size > 0) {
            // Clean up previous blob URL
            if (this.blobUrl) {
              URL.revokeObjectURL(this.blobUrl);
            }
            
            // Create a blob URL for the PDF to avoid CORS issues
            const blob = new Blob([response.data], { type: 'application/pdf' });
            this.blobUrl = URL.createObjectURL(blob);
            
            // console.log('PDF successfully loaded, created blob URL:', this.blobUrl);
            return true;
          } else {
            throw new Error(`PDF not ready: ${response.status}, size: ${response.data.size}`);
          }
        } catch (error) {
          // console.error(`PDF validation failed:`, {
          //   message: error.message,
          //   status: error.response && error.response.status,
          //   statusText: error.response && error.response.statusText,
          //   url: url
          // });
          
          // If CORS error and we're using primary API, immediately switch to fallback
          if (error.message.includes('CORS') || error.message.includes('Network Error')) {
            // console.log('CORS detected on primary API PDF - this indicates we should use fallback API');
            
            // If this is a preview document from primary API and we get CORS, 
            // the primary API generated a PDF but it's not accessible due to CORS
            // Let's immediately try the fallback API instead
            if (!this.usingFallbackAPI && this.safeParams && this.selectedPDF.type === "preview") {
              // console.log('Switching to fallback API due to CORS on primary API PDF');
              return false; // This will trigger fallback in getPreviewDocument
            }
            
            // For non-preview or fallback API, let vue-pdf-app try to handle it
            // console.log('Letting vue-pdf-app handle CORS for non-preview document');
            return true;
          }
          
          return false;
        }
      },
      async callFallbackPrintAPI(params) {
        // console.log('=== FALLBACK PRINT API CALLED ===');
        // console.log('Fallback API attempt:', this.fallbackRetryCount + 1, '/', this.FALLBACK_MAX_RETRIES);
        // console.log('Fallback API params:', params);
        
        try {
          // Update loading message to inform user about fallback
          this.loadingMessage = 'Switching to new print system.\nYour document will be ready shortly...';
          this.usingFallbackAPI = true;
          this.usedFallbackAPI = true; // Mark that fallback was used in this session
          
          // Construct fallback API URL
          const fallbackUrl = `https://api-saturn-eastus.azurewebsites.net/api/vnext/print`;
          
          // Extract tenant and user IDs from various sources
          const tenantId = this.tenantId || params.tenantId || (this.document && this.document.t);
          const userId = this.userId || params.userId || (this.document && this.document.u);
          
          // console.log('Fallback API - Using tenant ID:', tenantId);
          // console.log('Fallback API - Using user ID:', userId);
          
          if (!tenantId || !userId) {
            // console.error('Fallback API - Missing required tenant or user ID');
            throw new Error('Missing tenant or user ID for fallback API');
          }
          
          const queryParams = new URLSearchParams({
            t: tenantId,
            u: userId,
            s: 'Jupiter',
            uid: 1, // Hardcoded as number 1
            userID: 1, // Hardcoded as number 1
            id: params.id,
            moduleid: params.moduleId
          });
          
          const fullUrl = `${fallbackUrl}?${queryParams.toString()}`;
          // console.log('Fallback API URL:', fullUrl);
          
          // Call fallback API with extended timeout due to potential server startup delays
          const response = await axios.get(fullUrl, {
            timeout: 60000, // 60 second timeout for fallback API
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
              'x-functions-key': 'riR4vyP8VWAfe4rl59e6mauU0sYaxN8V2O8yKv8fsWGZ3YuYdaFUkw=='
            }
          });
          
          // console.log('Fallback API response status:', response.status);
          // console.log('Fallback API response data:', response.data);
          
          if (response.data && response.data.success && response.data.resources) {
            const document = response.data.resources;
            // console.log('Fallback API document received:', document);
            
            // Transform response to match expected format
            const transformedDocument = {
              documentId: document.documentId,
              linkID: document.linkID,
              documentName: document.documentName,
              description: document.description,
              documentType: document.documentType,
              intDocumentTypeID: document.intDocumentTypeID,
              url: document.url,
              wordURL: document.wordURL,
              referenceNumber: document.referenceNumber || '',
              utcCreatedDate: document.utcCreatedDate,
              printCode: document.printCode,
              moduleID: document.moduleID,
              t: this.tenantId,
              u: this.userId,
              source: 'fallback-api'
            };
            
            // console.log('Transformed fallback document:', transformedDocument);
            return transformedDocument;
          } else {
            throw new Error(`Fallback API returned unsuccessful response: ${JSON.stringify(response.data)}`);
          }
          
        } catch (error) {
          // console.error('Fallback API error:', {
          //   message: error.message,
          //   status: error.response && error.response.status,
          //   statusText: error.response && error.response.statusText,
          //   data: error.response && error.response.data,
          //   attempt: this.fallbackRetryCount + 1,
          //   maxRetries: this.FALLBACK_MAX_RETRIES
          // });
          
          if (error.code === 'ECONNABORTED') {
            // console.error('Fallback API timeout - server may be starting up');
            this.loadingMessage = 'New print system is starting up. This may take up to a minute...';
          }
          
          throw error;
        }
      },
      openHandler(pdf) {
        let outerContainerHtml = document.getElementById("outerContainer");
        if (outerContainerHtml) {
          outerContainerHtml.classList.add("sidebarOpen");
        }
        if (pdf && !pdf.pdfSidebar.isOpen) {
          this.config.sidebar = getSidebar();
          pdf.pdfSidebar.isOpen = true;
        }
      },
      onPDFError(error) {
        // console.error("PDF loading error in vue-pdf-app:", error);
        // console.log('Current PDF URL:', this.pdfURL);
        // console.log('Current primary retry count:', this.retryCount);
        // console.log('Current fallback retry count:', this.fallbackRetryCount);
        // console.log('Using fallback API:', this.usingFallbackAPI);
        
        // Calculate total remaining retries
        const totalMaxRetries = this.MAX_RETRIES + this.FALLBACK_MAX_RETRIES;
        const totalCurrentRetries = this.retryCount + this.fallbackRetryCount;
        
        // If PDF fails to load in the viewer and we have retries left, try again
        if (totalCurrentRetries < totalMaxRetries && this.selectedPDF && this.selectedPDF.url) {
          // console.log('Retrying due to PDF viewer error...');
          this.loading = true;
          this.loadingMessage = 'PDF viewer error, retrying...';
          
          setTimeout(async () => {
            const isValid = await this.validatePDFUrl(this.selectedPDF.url);
            if (isValid) {
              this.loading = false;
              this.loadingMessage = 'Loading PDF document...';
            }
          }, 2000);
        } else {
          // console.error('Max retries reached due to PDF viewer errors or no URL available');
          this.loadingMessage = 'Loading PDF document...';
        }
      },
      onLoadingFailed(error) {
        // console.error("PDF loading failed event:", error);
        // This is an additional error handler for loading failures
        this.onPDFError(error);
      },
      retryLoad() {
        // console.log('=== MANUAL RETRY REQUESTED ===');
        // console.log('Resetting all retry counters and API states');
        
        // Reset all retry states
        this.retryCount = 0;
        this.fallbackRetryCount = 0;
        this.usingFallbackAPI = false;
        this.usedFallbackAPI = false; // Reset fallback usage tracking on manual retry
        this.loading = true;
        this.loadingMessage = 'Loading PDF document...';
        
        if (this.safeParams && this.selectedPDF.type === "preview") {
          // console.log('Manual retry: Starting preview document generation with primary API');
          this.getPreviewDocument();
        } else if (this.selectedPDF && this.selectedPDF.url) {
          // console.log('Manual retry: Validating existing PDF URL');
          this.validatePDFUrl(this.selectedPDF.url).then((isValid) => {
            this.loading = false;
            if (!isValid) {
              // console.error('Manual retry failed');
            } else {
              // console.log('Manual retry successful');
            }
          });
        }
      },
      viewPlaceholders() {
        this.placeholderLoading = true;
        this.showPlaceholders = true;  // Set this to true immediately

        // Define module ID mappings based on keywords
        const moduleMappings = {
          quote: 6,
          project: 2,
          invoice: 26,
          purchaseorder: 7,
        };

        let moduleId = 6; // Default module ID

        // Check if `selectedPDF.url` contains any keyword and assign the corresponding module ID
        for (const key in moduleMappings) {
          if (this.selectedPDF.url.toLowerCase().includes(key)) {
            moduleId = moduleMappings[key];
            break;
          }
        }
        // console.log("Print url", this.selectedPDF.url);

        let urlObj = new URL(this.selectedPDF.url);

     

        // Dynamically compose print placeholder URL
        let url = `/print/document/generator?moduleid=${moduleId}&id=${this.selectedPDF.linkID}`;
        url += `&userid=1&u=${this.userId}&t=${this.tenantId}`;

        // Headers for API request
        const headers = {
          "x-functions-key": "LaVFhUo4k2uFcWsn8izsOiBWBLR4da35UpEZI3RWfxIVxx7rlIhCRg=="
        };

        // Make API GET request
        axios.get(url, { headers })
          .then((response) => {
            this.placeholderData = response.data.resources;
          })
          .catch((error) => {
            // console.error("Error fetching placeholders:", error);
            this.showPlaceholders = false; // Reset on error
          })
          .finally(() => {
            this.placeholderLoading = false;
          });
      },
      async downloadPDF() {
        const downloadUrl = this.originalPDFUrl || (this.selectedPDF && this.selectedPDF.url);
        
        if (!downloadUrl) {
          return;
        }
        
        try {
          const response = await axios.get(downloadUrl, {
            responseType: "arraybuffer",
          });
          const blob = new Blob([response.data], {type: "application/pdf"});
          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          
          // Improved filename logic with multiple fallbacks
          let filename = 'document.pdf';
          
          if (this.selectedPDF) {
            // Try different possible name properties
            filename = this.selectedPDF.documentName || 
                      this.selectedPDF.name || 
                      this.selectedPDF.fileName || 
                      this.selectedPDF.description ||
                      'document.pdf';
            
            // If filename doesn't have .pdf extension, add it
            if (filename && !filename.toLowerCase().endsWith('.pdf')) {
              filename += '.pdf';
            }
            
            // Replace "generated document" with a more descriptive name if possible
            if (filename.toLowerCase().includes('generated document')) {
              const referenceNumber = this.selectedPDF.referenceNumber;
              const documentType = this.selectedPDF.documentType;
              
              if (referenceNumber && documentType) {
                filename = `${documentType}_${referenceNumber}.pdf`;
              } else if (referenceNumber) {
                filename = `${referenceNumber}.pdf`;
              } else if (documentType) {
                filename = `${documentType}.pdf`;
              } else {
                // Try to extract from URL or use timestamp
                const urlParts = downloadUrl.split('/');
                const urlFilename = urlParts[urlParts.length - 1];
                if (urlFilename && urlFilename.includes('.pdf')) {
                  filename = urlFilename.split('?')[0]; // Remove query parameters
                } else {
                  filename = `Document_${new Date().toISOString().slice(0, 10)}.pdf`;
                }
              }
            }
          }
          
          link.download = filename;
          link.click();
        } catch (error) {
          // Silent error handling for production
        }
      },
      onOpenInWord() {
        if (!this.selectedPDF.wordURL) return;
        
        // Detect if the user is on Windows or Mac
        const isWindows = navigator.platform.indexOf('Win') > -1;
        const isMac = navigator.platform.indexOf('Mac') > -1;
        
        if (isWindows) {
          // Windows: Use the ms-word protocol
          var lnk = "ms-word:ofe|u|" + this.selectedPDF.wordURL + "|a|Web";
          window.location.href = lnk;
        } else if (isMac) {
          // Mac: Open Word document in a new tab and show instructions
          window.open(this.selectedPDF.wordURL, '_blank');
          
          // Show a helpful message for Mac users
          this.$bvToast.toast('The Word document has been opened in a new tab. To open it in Microsoft Word, click "Open in Word" from the web version.', {
            title: 'Open in Word - Mac',
            variant: 'info',
            autoHideDelay: 8000,
            solid: true
          });
        } else {
          // Other platforms: Just open in new tab
          window.open(this.selectedPDF.wordURL, '_blank');
        }
      },
      openInNewTab() {
        // console.log('Opening PDF in new tab...');
        const openUrl = this.originalPDFUrl || (this.selectedPDF && this.selectedPDF.url);
        
        if (!openUrl) {
          // console.error('No URL available for new tab');
          return;
        }
        
        let routeData = this.$router.resolve({
          name: "pdf-preview", 
          query: {
            url: openUrl, 
            wordURL: this.selectedPDF.wordURL, 
            name: this.selectedPDF.documentName
          }
        });
        window.open(routeData.href, "_blank");
      },
      initializeData() {
        // console.log('=== INITIALIZING PDF MODAL DATA ===');
        this.loading = true;
        this.loadingMessage = 'Loading PDF document...';
        
        // Reset API states
        this.retryCount = 0;
        this.fallbackRetryCount = 0;
        this.usingFallbackAPI = false;
        this.usedFallbackAPI = false; // Reset fallback usage tracking
        
        this.modalStatus = this.$store.getters.getModalStatus;
        this.selectedPDF = this.modalStatus.data;
        
        // Store original params to prevent loss during retries
        if (this.selectedPDF && this.selectedPDF.params) {
          this.originalParams = { ...this.selectedPDF.params };
        }
        
        // console.log('Modal status:', this.modalStatus);
        // console.log('Selected PDF data:', this.selectedPDF);
        // console.log('Original params stored:', this.originalParams);

        if (this.safeParams && this.selectedPDF.type === "preview") {
          // console.log('Preview type detected, starting document generation with primary API...');
          //Delay for 3 before loading pdf, this will give the PDF generator time to finish
          //This will help  prevent the PDF from loading before the PDF generator is finished
          const sleep = (time) => new Promise((res) => setTimeout(res, time, "done sleeping"));
          sleep(2000).then(() => this.getPreviewDocument());
        } else {
          // console.log('Non-preview type, checking if PDF needs validation...');
          
          if (this.selectedPDF && this.selectedPDF.url) {
            // Store original URL and validate existing PDF
            this.originalPDFUrl = this.selectedPDF.url;
            // console.log('Existing PDF URL found, validating:', this.originalPDFUrl);
            
            this.validatePDFUrl(this.selectedPDF.url).then((isValid) => {
              if (isValid) {
                // console.log('Existing PDF validation successful');
              } else {
                // console.log('Existing PDF validation failed');
              }
              this.loading = false;
            });
          } else {
            this.loading = false;
          }
        }
        
        // Check if it's an Aircool system
        if (this.selectedPDF.url && this.selectedPDF.url.includes(this.aircoolTenantId)) {
          this.isAircoolSystem = true;
          // console.log('Aircool system detected');
        }
      },
       
      async getPreviewDocument() {
        const currentAttempt = this.usingFallbackAPI ? this.fallbackRetryCount + 1 : this.retryCount + 1;
        const maxRetries = this.usingFallbackAPI ? this.FALLBACK_MAX_RETRIES : this.MAX_RETRIES;
        const apiType = this.usingFallbackAPI ? 'FALLBACK' : 'PRIMARY';
        
        // console.log(`=== ${apiType} API ATTEMPT ${currentAttempt}/${maxRetries} ===`);
        // console.log('Getting preview document - params check:', this.safeParams);
        
        // Check if we have valid params
        if (!this.safeParams) {
          // console.error('No valid params available for document generation');
          this.loading = false;
          return;
        }
        
        try {
          let document;
          
          if (this.usingFallbackAPI) {
            // Use fallback API
            // console.log('Using fallback print API...');
            document = await this.callFallbackPrintAPI(this.safeParams);
            // Don't increment here - will be incremented after validation
          } else {
            // Use primary API
            // console.log('Using primary print API...');
            this.loadingMessage = 'Generating PDF document...';
            document = await app.print(this.safeParams.moduleId, this.safeParams.id);
            // Don't increment here - will be incremented after validation
          }
          
          // console.log(`${apiType} API response:`, document);
          this.document = document;

          if (document && document.t && document.u) {
            this.tenantId = document.t;
            this.userId = document.u;
          }

          if (document && document.url) {
            // Increment counters only after successful document generation
            if (this.usingFallbackAPI) {
              this.fallbackRetryCount++;
              // console.log(`Fallback API attempt ${this.fallbackRetryCount}/${this.FALLBACK_MAX_RETRIES} completed`);
            } else {
              this.retryCount++;
              // console.log(`Primary API attempt ${this.retryCount}/${this.MAX_RETRIES} completed`);
            }
            // Store original URL
            this.originalPDFUrl = document.url;
            
            const updatedData = {
              ...this.modalStatus,
              data: document,
            };

            this.$store.commit("setModalStatus", updatedData);
            this.modalStatus = this.$store.getters.getModalStatus;
            this.selectedPDF = this.modalStatus.data;

            //Append a unique URL to the pdf to prevent browser caching
            const urlWithTimestamp = document.url + "?v=" + new Date().getTime();
            this.selectedPDF.url = urlWithTimestamp;
            
            // console.log(`Updated PDF URL with timestamp (${apiType}):`, urlWithTimestamp);

            if(this.selectedPDF.url.includes(this.aircoolTenantId)) {
              this.isAircoolSystem = true;
            }

            // Update loading message for validation
            this.loadingMessage = 'Validating PDF document...';

            // Validate the PDF URL and create blob URL
            const isValid = await this.validatePDFUrl(this.selectedPDF.url);
            
            if (isValid) {
              // console.log(`PDF validation successful with ${apiType} API, stopping retries`);
              this.loading = false;
              this.clearTimer();
              this.retryCount = 0;
              this.fallbackRetryCount = 0;
              this.usingFallbackAPI = false; // Stop using fallback but keep usedFallbackAPI true
              this.loadingMessage = 'Loading PDF document...';
            } else {
              // PDF validation failed
              // Check if this is a CORS/network error that should trigger immediate fallback
              const isCorsOrNetworkError = !isValid && !this.usingFallbackAPI;
              
              if (isCorsOrNetworkError) {
                // Immediate switch to fallback for validation failures (likely CORS)
                // console.log('=== PDF VALIDATION FAILED (LIKELY CORS) - IMMEDIATE SWITCH TO FALLBACK ===');
                this.usingFallbackAPI = true;
                this.usedFallbackAPI = true;
                this.fallbackRetryCount = 0;
                this.loadingMessage = 'PDF not accessible. Switching to new print system...';
                
                this.timer = setTimeout(() => {
                  this.getPreviewDocument();
                }, 1000);
              } else if (!this.usingFallbackAPI && (this.retryCount < this.MAX_RETRIES)) {
                // Continue retrying with primary API for non-CORS issues
                // console.log(`PDF validation failed for ${apiType} API, retrying...`);
                this.timer = setTimeout(() => {
                  this.getPreviewDocument();
                }, 2000);
              } else {
                // Fallback API validation also failed or primary API exhausted
                // console.error('PDF validation failed for both primary and fallback APIs');
                this.loading = false;
                this.loadingMessage = 'Loading PDF document...';
              }
            }
          } else {
            // Document generation didn't return a URL, continue retrying
            // Increment counters for failed attempts too
            if (this.usingFallbackAPI) {
              this.fallbackRetryCount++;
              // console.log(`Fallback API attempt ${this.fallbackRetryCount}/${this.FALLBACK_MAX_RETRIES} - no URL returned`);
            } else {
              this.retryCount++;
              // console.log(`Primary API attempt ${this.retryCount}/${this.MAX_RETRIES} - no URL returned`);
            }
            
            const shouldRetry = this.usingFallbackAPI ? 
              this.fallbackRetryCount < this.FALLBACK_MAX_RETRIES : 
              this.retryCount < this.MAX_RETRIES;
              
            if (shouldRetry) {
              // console.log(`Document not ready from ${apiType} API, retrying...`);
              this.timer = setTimeout(() => {
                this.getPreviewDocument();
              }, 3000); // Increased delay for fallback API
            } else if (!this.usingFallbackAPI) {
              // Primary API exhausted, switch to fallback
              // console.log('=== PRIMARY API EXHAUSTED - SWITCHING TO FALLBACK ===');
              this.usingFallbackAPI = true;
              this.usedFallbackAPI = true; // Mark that fallback was used
              this.fallbackRetryCount = 0;
              this.loadingMessage = 'Primary print system unavailable. Switching to new print system...';
              
              this.timer = setTimeout(() => {
                this.getPreviewDocument();
              }, 2000);
            } else {
              // console.error('Failed to load document after maximum retries on both APIs');
              this.loading = false;
              this.loadingMessage = 'Loading PDF document...';
            }
          }
        } catch (e) {
          // console.error(`Error in getPreviewDocument (${apiType} API):`, e);
          
          // Check if this is a CORS or network error that should trigger immediate fallback
          const isCorsOrNetworkError = e.message && (
            e.message.includes('CORS') || 
            e.message.includes('Network Error') ||
            e.message.includes('net::ERR_FAILED') ||
            (e.response && e.response.status === 0)
          );
          
          if (!this.usingFallbackAPI && isCorsOrNetworkError) {
            // Immediate switch to fallback for CORS/network errors
            // console.log('=== CORS/NETWORK ERROR DETECTED - IMMEDIATE SWITCH TO FALLBACK ===');
            this.usingFallbackAPI = true;
            this.usedFallbackAPI = true;
            this.fallbackRetryCount = 0;
            this.loadingMessage = 'Network/CORS issue detected. Switching to new print system...';
            
            this.timer = setTimeout(() => {
              this.getPreviewDocument();
            }, 1000); // Shorter delay for immediate switch
            return;
          }
          
          // Increment counters for non-CORS/network failed attempts
          if (this.usingFallbackAPI) {
            this.fallbackRetryCount++;
            // console.log(`Fallback API attempt ${this.fallbackRetryCount}/${this.FALLBACK_MAX_RETRIES} - error occurred`);
          } else {
            this.retryCount++;
            // console.log(`Primary API attempt ${this.retryCount}/${this.MAX_RETRIES} - error occurred`);
          }
          
          // Determine if we should retry or switch APIs
          const shouldRetryCurrentAPI = this.usingFallbackAPI ? 
            this.fallbackRetryCount < this.FALLBACK_MAX_RETRIES : 
            this.retryCount < this.MAX_RETRIES;
            
          if (shouldRetryCurrentAPI) {
            // console.log(`Retrying ${apiType} API due to error - attempt ${currentAttempt}/${maxRetries}`);
            this.timer = setTimeout(() => {
              this.getPreviewDocument();
            }, this.usingFallbackAPI ? 5000 : 2000); // Longer delay for fallback retries
          } else if (!this.usingFallbackAPI) {
            // Primary API failed all retries, switch to fallback
            // console.log('=== PRIMARY API FAILED - SWITCHING TO FALLBACK ===');
            this.usingFallbackAPI = true;
            this.usedFallbackAPI = true; // Mark that fallback was used
            this.fallbackRetryCount = 0;
            this.loadingMessage = 'Primary print system failed. Trying new print system...';
            
            this.timer = setTimeout(() => {
              this.getPreviewDocument();
            }, 2000);
          } else {
            // console.error('Failed to load document after maximum retries on both PRIMARY and FALLBACK APIs:', e);
            this.loading = false;
            this.loadingMessage = 'Loading PDF document...';
            this.usingFallbackAPI = false;
          }
        }
      },
      clearTimer() {
        if (this.timer) {
          clearTimeout(this.timer);
          this.timer = null;
        }
      },
      onPrint() {
        // console.log('Printing PDF...');
        const printUrl = this.originalPDFUrl || (this.selectedPDF && this.selectedPDF.url);
        
        if (!printUrl) {
          // console.error('No URL available for printing');
          return;
        }
        
        // console.log('Print URL:', printUrl);
        print(printUrl);
      },
      onForcePrint() {
        // Force print always uses the new print service (fallback API)
        if (!this.safeParams) {
          // console.error('No params available for force print');
          return;
        }
        
        // Force the system to use the fallback API immediately
        this.usingFallbackAPI = true;
        this.usedFallbackAPI = true;
        this.retryCount = this.MAX_RETRIES; // Skip primary API retries
        this.fallbackRetryCount = 0;
        this.loading = true;
        this.loadingMessage = 'Force printing with new print service...';
        
        // Start document generation with fallback API
        this.getPreviewDocument();
      },
      async onOpenVNextPrint() {
        // Open the vNext print document in a new tab after generating it
        if (!this.safeParams) {
          // console.error('No params available for vNext print document');
          return;
        }
        
        // Show toast notification that we're generating the document
        this.$bvToast.toast('Generating document with new print system...', {
          title: 'Opening Document',
          variant: 'info',
          autoHideDelay: 3000,
          solid: true
        });
        
        try {
          // Call the fallback API to generate the document
          const document = await this.callFallbackPrintAPI(this.safeParams);
          
          if (document && document.url) {
            // Open the generated PDF in a new tab
            window.open(document.url, '_blank');
            
            // Show success message
            this.$bvToast.toast('Document opened in new tab', {
              title: 'Success',
              variant: 'success',
              autoHideDelay: 2000,
              solid: true
            });
          } else {
            throw new Error('No PDF URL returned from new print system');
          }
        } catch (error) {
          // console.error('Error generating vNext print document:', error);
          
          // Show error message
          this.$bvToast.toast('Failed to generate document with new print system. Please try again.', {
            title: 'Error',
            variant: 'danger',
            autoHideDelay: 5000,
            solid: true
          });
        }
      },
    },
  };
</script>
<style>
.key {
  min-width: 450px !important;
  white-space: nowrap;
  font-weight: bold;
}

.spinner-border {
  width: 3rem;
  height: 3rem;
}

/* PDF Container Layout */
.pdf-container {
  height: 80vh !important;
  max-height: 80vh !important;
  min-height: 0 !important;
  overflow: hidden !important;
}

/* PDF Viewer Container Constraints */
.pdf-viewer-container {
  position: relative !important;
  box-sizing: border-box !important;
  height: 100% !important;
  min-height: 0 !important;
}

.pdf-viewer-container .vue-pdf-app {
  height: 100% !important;
  max-height: 100% !important;
  overflow: hidden !important;
}

.pdf-viewer-container #outerContainer {
  height: 100% !important;
  max-height: 100% !important;
  overflow: hidden !important;
}

.pdf-viewer-container #mainContainer {
  height: 100% !important;
  max-height: 100% !important;
}

.pdf-viewer-container #viewerContainer {
  height: 100% !important;
  max-height: 100% !important;
  overflow: auto !important;
}

/* Custom loading styles */
.loading-holder {
  min-height: 400px;
}

.loading-dots {
  white-space: pre-line; /* Preserve line breaks */
}

.loading-dots:after {
  content: ".";
  animation: dots 1s steps(5, end) infinite;
}

@keyframes dots {
  0%, 20% {
    color: rgba(0, 0, 0, 0);
    text-shadow: 0.25em 0 0 rgba(0, 0, 0, 0), 0.5em 0 0 rgba(0, 0, 0, 0);
  }
  40% {
    color: #6e0677;
    text-shadow: 0.25em 0 0 rgba(0, 0, 0, 0), 0.5em 0 0 rgba(0, 0, 0, 0);
  }
  60% {
    text-shadow: 0.25em 0 0 #6e0677, 0.5em 0 0 rgba(0, 0, 0, 0);
  }
  80%, 100% {
    text-shadow: 0.25em 0 0 #6e0677, 0.5em 0 0 #6e0677;
  }
}

.text-warning {
  color: #ffc107 !important;
}

/* Print system notice bar */
.print-system-notice {
  background-color: #6e0677;
  color: white;
  border-bottom: 1px solid #5a0563;
}

.print-system-notice .fa-info-circle {
  color: #ffffff;
}

/* Force Print Button Styling */
.btn-outline-warning {
  border-color: #ff8c00 !important;
  color: #ff8c00 !important;
  border-width: 0.5px !important;
  border-radius: 0.375rem !important;
}

.btn-outline-warning:hover {
  background-color: rgba(255, 140, 0, 0.1) !important;
  border-color: #ff8c00 !important;
  color: #ff8c00 !important;
}

.btn-outline-warning:focus,
.btn-outline-warning.focus {
  box-shadow: 0 0 0 0.2rem rgba(255, 140, 0, 0.25) !important;
}

/* Actions Dropdown Styling */
.actions-dropdown .btn {
  border-radius: 0.375rem !important;
}

.actions-dropdown .dropdown-menu {
  z-index: 9999 !important;
  border-radius: 0.375rem !important;
}

/* Modal action bar z-index fix */
.modal-action-bar_pdf {
  position: relative;
  z-index: 1000;
}

/* PDF Viewer Sidebar Styling */
.pdf-container .vue-pdf-app #sidebarContainer {
  width: 150px !important;
}

.pdf-container .vue-pdf-app #viewerContainer {
  left: 150px !important;
}

/* Thumbnail styling */
.pdf-container .vue-pdf-app .thumbnail {
  margin-bottom: 10px !important;
}

.pdf-container .vue-pdf-app .thumbnailSelectionRing {
  border: 2px solid #6e0677 !important;
}
</style>